import type { PermalinkListingSlugs } from '@/features/seo-listing/consts';
import type { ListingFiltersInput, ListingFiltersOutput } from '../../types';

/**
 * Converts selected filter IDs to optional array (undefined if empty)
 */
const toOptionalSelectedIds = (selectedIds: string[]): string[] | undefined =>
  selectedIds.length > 0 ? selectedIds : undefined;

const resolveLockedSlug = (
  permalinkSlug: string | undefined,
  selectedIds: string[],
): string | undefined => (permalinkSlug && selectedIds.length > 0 ? permalinkSlug : undefined);

/**
 * Builds listing filters for API requests, handling SEO permalink routes.
 * On SEO permalinks fanfics-custom/v2 must receive path slugs (tagSlug, etc.),
 * not the numeric ids restored into filter state from listingGetUrl.
 * If the user cleared that facet, the path slug is dropped even before
 * history.pushState leaves the permalink.
 */
export const buildPermalinkAwareListingFilters = (
  listingSlugs: PermalinkListingSlugs | null,
  filters: ListingFiltersInput,
): ListingFiltersOutput => {
  const lockedFandomSlug = resolveLockedSlug(listingSlugs?.fandomSlug, filters.selectedFandoms);
  const lockedCharacterSlug = lockedFandomSlug
    ? resolveLockedSlug(listingSlugs?.characterSlug, filters.selectedCharacters)
    : undefined;
  const lockedPairingSlug = lockedFandomSlug
    ? resolveLockedSlug(listingSlugs?.pairingSlug, filters.selectedPairingCharacters)
    : undefined;
  const lockedTagSlug = resolveLockedSlug(listingSlugs?.tagSlug, filters.selectedTags);

  return {
    search: filters.search.trim() || undefined,
    sortBy: filters.sortBy,
    fandoms: lockedFandomSlug ? undefined : toOptionalSelectedIds(filters.selectedFandoms),
    tags: lockedTagSlug ? undefined : toOptionalSelectedIds(filters.selectedTags),
    universes: toOptionalSelectedIds(filters.selectedUniverses),
    statuses: toOptionalSelectedIds(filters.selectedStatuses),
    ratings: toOptionalSelectedIds(filters.selectedRatings),
    directions: toOptionalSelectedIds(filters.selectedDirections),
    characters: lockedCharacterSlug ? undefined : toOptionalSelectedIds(filters.selectedCharacters),
    pairingCharacters: lockedPairingSlug
      ? undefined
      : toOptionalSelectedIds(filters.selectedPairingCharacters),
    fandomSlug: lockedFandomSlug,
    characterSlug: lockedCharacterSlug,
    pairingSlug: lockedPairingSlug,
    tagSlug: lockedTagSlug,
  };
};
